import React, { useCallback, useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CampaignIcon from "@mui/icons-material/Campaign";
import "../../../../css/forms.css";
import "../../../../css/cards.css";
import "../../../../css/buttons.css";
import "../../../../css/layout.css";
import "../../../../css/tables.css";

import {
  ApiError,
  api,
  cancelCampaign as cancelCampaignRequest,
  deleteCampaign as deleteCampaignRequest,
} from "../../../../api/apiClient";
import PageHeader from "../../../../app/layout/PageHeader";
import "../CommunicationList.css";

interface CampaignListViewProps {
  onCreate: () => void;
  onEdit: (id: number) => void;
  onViewReport?: (id: number) => void;
  onBackToDashboard: () => void;
}

interface Campaign {
  campaignId: number;
  name: string;
  status: string;
  createdDate: string;
  scheduledSendDate?: string | null;
  scheduledDate?: string | null;
  scheduledTime?: string | null;
  scheduledAtUtc?: string | null;
  serviceCommunicationType?: "PreRegistration" | "PostRegistration" | null;
}

const getStatus = (campaign: Campaign) => campaign.status?.toLowerCase() ?? "";

const getScheduledSendDate = (campaign: Campaign) => {
  if (campaign.scheduledAtUtc) {
    return new Date(campaign.scheduledAtUtc);
  }

  if (campaign.scheduledSendDate) {
    return new Date(campaign.scheduledSendDate);
  }

  if (campaign.scheduledDate) {
    const scheduledDateTime = campaign.scheduledTime
      ? `${campaign.scheduledDate}T${campaign.scheduledTime}`
      : campaign.scheduledDate;

    return new Date(scheduledDateTime);
  }

  return null;
};

const hasFutureScheduledSend = (campaign: Campaign) => {
  const scheduledSendDate = getScheduledSendDate(campaign);

  return (
    scheduledSendDate instanceof Date &&
    !Number.isNaN(scheduledSendDate.getTime()) &&
    scheduledSendDate > new Date()
  );
};

const canEditCampaign = (campaign: Campaign) => {
  const status = getStatus(campaign);
  return status === "draft" ||
    (status === "scheduled" && hasFutureScheduledSend(campaign));
};

const canDeleteCampaign = (campaign: Campaign) => {
  return getStatus(campaign) === "draft";
};

const canCancelCampaign = (campaign: Campaign) => {
  const status = getStatus(campaign);
  return status === "scheduled" || status === "queued" ||
    (status === "active" && campaign.serviceCommunicationType === "PostRegistration");
};

const canViewReport = (campaign: Campaign) => {
  const status = getStatus(campaign);

  return status === "queued" || status === "processed";
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof ApiError) {
    if (typeof error.body === "string" && error.body.trim()) {
      return error.body;
    }

    if (
      error.body &&
      typeof error.body === "object" &&
      "message" in error.body &&
      typeof error.body.message === "string"
    ) {
      return error.body.message;
    }
  }

  return fallback;
};

export default function CampaignListView({
  onCreate,
  onEdit,
  onViewReport,
  onBackToDashboard,
}: CampaignListViewProps) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadCampaigns = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await api<Campaign[]>("/api/campaign/GetAll", {
          method: "GET",
        });
        if (!isMounted) return;

        setCampaigns(data);
      } catch {
        if (!isMounted) return;

        setCampaigns([]);
        setError("Failed to load campaigns");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCampaigns();

    return () => {
      isMounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return campaigns;

    return campaigns.filter((campaign) =>
      `${campaign.name} ${campaign.status}`.toLowerCase().includes(term),
    );
  }, [campaigns, search]);

  const deleteCampaign = useCallback(async (campaign: Campaign) => {
    if (!canDeleteCampaign(campaign)) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete "${campaign.name}"?\n\nThis action cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      await deleteCampaignRequest(campaign.campaignId);

      setCampaigns((current) =>
        current.filter((item) => item.campaignId !== campaign.campaignId),
      );
      setError(null);
    } catch (deleteError) {
      setError(getApiErrorMessage(deleteError, "Failed to delete campaign"));
    }
  }, []);

  const cancelCampaign = useCallback(async (campaign: Campaign) => {
    if (!canCancelCampaign(campaign)) return;

    const confirmed = window.confirm(
      `Cancel "${campaign.name}"?\n\nMessages that have already been submitted to the SMS provider cannot be cancelled.`,
    );
    if (!confirmed) return;

    try {
      await cancelCampaignRequest(campaign.campaignId);
      setCampaigns((current) =>
        current.map((item) =>
          item.campaignId === campaign.campaignId
            ? { ...item, status: "Cancelled" }
            : item,
        ),
      );
      setError(null);
    } catch (cancelError) {
      setError(getApiErrorMessage(cancelError, "Failed to cancel campaign"));
    }
  }, []);

  const viewReport = useCallback(
    (campaign: Campaign) => {
      if (!canViewReport(campaign)) return;

      onViewReport?.(campaign.campaignId);
    },
    [onViewReport],
  );

  const renderCampaignActions = useCallback(
    (campaign: Campaign) => {
      const showEdit = canEditCampaign(campaign);
      const showDelete = canDeleteCampaign(campaign);
      const showCancel = canCancelCampaign(campaign);
      const showReport = canViewReport(campaign);

      return (
        <div className="communication-list-actions">
          {showEdit && (
            <Tooltip title="Edit campaign">
              <button
                type="button"
                onClick={() => onEdit(campaign.campaignId)}
                aria-label="Edit campaign"
              >
                <EditIcon fontSize="small" />
              </button>
            </Tooltip>
          )}

          {showDelete && (
            <Tooltip title="Delete campaign">
              <button
                type="button"
                className="communication-list-action--danger"
                onClick={() => deleteCampaign(campaign)}
                aria-label="Delete campaign"
              >
                <DeleteIcon fontSize="small" />
              </button>
            </Tooltip>
          )}

          {showCancel && (
            <Tooltip title="Cancel campaign">
              <button
                type="button"
                className="communication-list-action--danger"
                onClick={() => cancelCampaign(campaign)}
                aria-label="Cancel campaign"
              >
                <CancelScheduleSendIcon fontSize="small" />
              </button>
            </Tooltip>
          )}

          {showReport && (
            <Tooltip
              title={
                onViewReport
                  ? "View campaign report"
                  : "Campaign report is not available yet"
              }
            >
              <span>
                <button
                  type="button"
                  onClick={() => viewReport(campaign)}
                  disabled={!onViewReport}
                  aria-label="View campaign report"
                >
                  <AssessmentIcon fontSize="small" />
                </button>
              </span>
            </Tooltip>
          )}
        </div>
      );
    },
    [cancelCampaign, deleteCampaign, onEdit, onViewReport, viewReport],
  );

  return (
    <div className="page-container">
      <PageHeader
        title="Campaigns"
        subtitle="Manage communications"
        actions={
          <div className="service-management-header-actions">
            <button
              className="icon-button-solid icon-button-solid--success"
              onClick={onCreate}
              title="Create Campaign"
            >
              <AddIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="icon-button-muted-solid"
              onClick={onBackToDashboard}
              title="Back to dashboard"
              aria-label="Back to dashboard"
            >
              <ArrowBackIcon fontSize="small" />
            </button>
          </div>
        }
      />
      <div className="card">
        <div className="card-header-row">
          <div className="table-actions">
            <div className="search-container table-search">
              <div className="search-input-wrapper">
                <SearchIcon className="search-icon-left" fontSize="small" />
                <input
                  className="form-input search-input-with-icons"
                  placeholder="Search campaigns..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    className="search-clear-button"
                    onClick={() => setSearch("")}
                    aria-label="Clear campaign search"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && <div className="form-error">{error}</div>}

        <div className="communication-list">
          {loading && (
            <div className="communication-list-empty">Loading campaigns...</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="communication-list-empty">No campaigns found</div>
          )}
          {!loading && filtered.map((campaign) => (
            <article className="communication-list-item" key={campaign.campaignId}>
              <span className="communication-list-icon communication-list-icon--campaign">
                <CampaignIcon />
              </span>
              <div className="communication-list-copy">
                <div className="communication-list-title-row">
                  <strong>{campaign.name}</strong>
                  <span className={`communication-status communication-status--${getStatus(campaign)}`}>
                    {campaign.status}
                  </span>
                </div>
                <small>
                  Created {new Date(campaign.createdDate).toLocaleString()}
                </small>
                {getScheduledSendDate(campaign) && (
                  <span>
                    Scheduled {getScheduledSendDate(campaign)!.toLocaleString()}
                  </span>
                )}
              </div>
              {renderCampaignActions(campaign)}
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
